import { c as _c } from "react/compiler-runtime";
import { Button, Input } from '@common/components';
import { SvgChevronleft, SvgDelete, SvgExpandWindow, SvgHome, SvgKebab, SvgLogo, SvgMagnifyingGlass, SvgNotification, SvgSetting } from '@common/icon';
import { cn } from '@core/utils';
import { usePathname } from 'next/navigation';
import { type ReactNode, useEffect, useRef } from 'react';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { CustomLink } from '@/app/_components/CustomLink';
import SmallLogo from '@/assets/svgs/small_logo.svg';
import { DONATION_ROUTES, MODULE_ROUTES, ROUTES, SUB_DOMAIN } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useIsomorphicLayoutEffect } from '@/hooks/useIsomorphicLayoutEffect';
import { TossExitConfirmModal, useTossWebView } from '@/modules/toss';
import type { FlutterInAppWebViewWindow } from '@/modules/wello-app';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';
import { useGlobalStore } from '@/stores/GlobalStore';
import { findRoute } from '@/utils';
import { gtmDatasets } from '@/utils/analytics/gtm';
import { Image } from '../Image/Image';
const TOSS_LOGO_URL = 'https://cdn.welfarehello.com/static/toss-logo.png';
type NavigationRouter = Pick<ReturnType<typeof useCustomRouter>, 'push' | 'replace' | 'back'>;
export type PageNavigationContentProps = PageNavigationProps & {
  isToss: boolean;
  isKbWebview: boolean;
  pathname: string;
  setShareLink?: (link: string) => void;
  router?: NavigationRouter;
  openConfirmModal?: VoidFunction;
  closeConfirmModal?: VoidFunction;
  confirmAndClose?: VoidFunction;
  isModalOpened?: boolean;
};
interface PageNavigationStore {
  contentProps: PageNavigationContentProps | null;
  contentPropsByPathname: Record<string, PageNavigationContentProps>;
  ownerId: number | null;
  ownerVersion: number | null;
  setContent: (ownerId: number, ownerVersion: number, contentProps: PageNavigationContentProps) => void;
  clearContent: (ownerId?: number, ownerVersion?: number) => void;
}
let pageNavigationOwnerId = 0;
const createPageNavigationOwnerId = () => {
  pageNavigationOwnerId += 1;
  return pageNavigationOwnerId;
};
const usePageNavigationStore = create<PageNavigationStore>(set => ({
  contentProps: null,
  contentPropsByPathname: {},
  ownerId: null,
  ownerVersion: null,
  setContent: (ownerId, ownerVersion, contentProps) => set(state => ({
    contentProps,
    contentPropsByPathname: {
      ...state.contentPropsByPathname,
      [contentProps.pathname]: contentProps
    },
    ownerId,
    ownerVersion
  })),
  clearContent: (ownerId, ownerVersion) => set(state => {
    if (ownerId !== undefined && ownerVersion !== undefined && (state.ownerId !== ownerId || state.ownerVersion !== ownerVersion)) {
      return state;
    }
    const pathname = state.contentProps?.pathname;
    const contentPropsByPathname = {
      ...state.contentPropsByPathname
    };
    if (pathname) delete contentPropsByPathname[pathname];
    return {
      contentProps: null,
      contentPropsByPathname,
      ownerId: null,
      ownerVersion: null
    };
  })
}));
const PageNavigationContent = t0 => {
  const $ = _c(98);
  const {
    logo,
    logoHref,
    title,
    titleLeft,
    children,
    backButton: t1,
    backButtonType: t2,
    searchButton,
    bellButton,
    notification,
    shareButton,
    settingsButton,
    kebabButton,
    onClickBack,
    onClickKebab,
    hideHomeButton,
    className,
    style,
    textColor,
    isNotUsedHomeButton,
    donationHomeButton,
    backgroundColor,
    searchForm,
    searchFormDefaultValue,
    searchFormClassName,
    onSearchFormMount,
    onSearchFormClick,
    onSearchFormSubmit,
    isToss,
    pathname,
    setShareLink,
    router,
    openConfirmModal,
    closeConfirmModal,
    confirmAndClose,
    isModalOpened
  } = t0;
  const backButton = t1 === undefined ? true : t1;
  const backButtonType = t2 === undefined ? "back" : t2;
  const hasBottomNav = findRoute(pathname)?.hasBottomNav;
  const t3 = router?.push ?? _temp;
  const t4 = router?.replace ?? _temp2;
  const t5 = router?.back ?? _temp3;
  let t6;
  if ($[0] !== t3 || $[1] !== t4 || $[2] !== t5) {
    t6 = {
      push: t3,
      replace: t4,
      back: t5
    };
    $[0] = t3;
    $[1] = t4;
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const safeRouter = t6;
  let t7;
  if ($[4] !== setShareLink) {
    t7 = link => setShareLink?.(link);
    $[4] = setShareLink;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const handleShareLink = t7;
  let t8;
  if ($[6] !== openConfirmModal) {
    t8 = () => openConfirmModal?.();
    $[6] = openConfirmModal;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const handleOpenConfirm = t8;
  let t9;
  if ($[8] !== closeConfirmModal) {
    t9 = () => closeConfirmModal?.();
    $[8] = closeConfirmModal;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const handleCloseConfirm = t9;
  let t10;
  if ($[10] !== confirmAndClose) {
    t10 = () => confirmAndClose?.();
    $[10] = confirmAndClose;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const handleConfirmAndClose = t10;
  const modalOpened = Boolean(isModalOpened);
  const DEFAULT_LOGO_HREF = isToss ? DONATION_ROUTES.HOME.pathname : ROUTES.HOME.pathname;
  let t11;
  if ($[12] !== className) {
    t11 = cn("relative flex min-h-48 w-full items-center", "box-border bg-Gray-white text-center", className);
    $[12] = className;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const t12 = backgroundColor === "gray" ? "#f3f3f3" : undefined;
  let t13;
  if ($[14] !== style || $[15] !== t12) {
    t13 = {
      backgroundColor: t12,
      ...style
    };
    $[14] = style;
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== textColor || $[18] !== titleLeft) {
    t14 = titleLeft ? <h3 className={cn("absolute left-20 font-700 flex-center typo-h6", textColor)}>{titleLeft}</h3> : null;
    $[17] = textColor;
    $[18] = titleLeft;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== textColor || $[21] !== title) {
    t15 = title ? <h3 className={cn("absolute flex size-full items-center justify-center", "break-all text-center font-600 text-Gray-900 typo-body3", textColor)}>{title}</h3> : null;
    $[20] = textColor;
    $[21] = title;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== DEFAULT_LOGO_HREF || $[24] !== isToss || $[25] !== logo || $[26] !== logoHref) {
    t16 = logo ? <div className="flex items-center gap-12"><div className={cn("flex items-center gap-12", {
        "gap-2": isToss
      })}>{isToss ? <><Image src={TOSS_LOGO_URL} alt="toss" width={31} height={31} /><SvgDelete className="size-20 text-Gray-400" /></> : null}<CustomLink aria-label={"\uC6F0\uB85C"} href={logoHref ?? DEFAULT_LOGO_HREF} {...gtmDatasets.clickGnb({
          menu_name: "\uB85C\uACE0"
        })}>{logo === "symbol" ? <SmallLogo className="size-40" /> : <SvgLogo className="h-29 w-86" />}</CustomLink></div></div> : null;
    $[23] = DEFAULT_LOGO_HREF;
    $[24] = isToss;
    $[25] = logo;
    $[26] = logoHref;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== backButton || $[29] !== backButtonType || $[30] !== onClickBack || $[31] !== safeRouter || $[32] !== textColor) {
    t17 = backButton ? <Button aria-label={"\uB4A4\uB85C \uAC00\uAE30"} className="relative size-26 p-0" dimensions="H26" theme="primary-text" onClick={onClickBack ? typeof onClickBack === "string" ? () => safeRouter.replace(onClickBack) : onClickBack : () => safeRouter.back()}>{backButtonType === "back" ? <SvgChevronleft className={cn("size-24", textColor)} /> : <SvgDelete className={cn("size-24", textColor)} />}</Button> : null;
    $[28] = backButton;
    $[29] = backButtonType;
    $[30] = onClickBack;
    $[31] = safeRouter;
    $[32] = textColor;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== hasBottomNav || $[35] !== hideHomeButton || $[36] !== isNotUsedHomeButton || $[37] !== safeRouter || $[38] !== textColor) {
    t18 = !hasBottomNav && !isNotUsedHomeButton ? <Button aria-label={"\uD648\uC73C\uB85C \uAC00\uAE30"} className={cn("relative size-26 p-0", hideHomeButton && "hidden")} dimensions="H26" theme="primary-text" onClick={() => safeRouter.push(ROUTES.HOME.pathname)}><SvgHome className={cn("size-24", textColor)} /></Button> : null;
    $[34] = hasBottomNav;
    $[35] = hideHomeButton;
    $[36] = isNotUsedHomeButton;
    $[37] = safeRouter;
    $[38] = textColor;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== donationHomeButton || $[41] !== safeRouter || $[42] !== textColor) {
    t19 = donationHomeButton ? <Button aria-label={"\uACE0\uD5A5\uC0AC\uB791\uAE30\uBD80 \uD648\uC73C\uB85C \uAC00\uAE30"} className="relative size-26 p-0" dimensions="H26" theme="primary-text" onClick={() => safeRouter.push(DONATION_ROUTES.HOME.pathname)}><SvgHome className={cn("size-24", textColor)} /></Button> : null;
    $[40] = donationHomeButton;
    $[41] = safeRouter;
    $[42] = textColor;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== onSearchFormClick || $[45] !== onSearchFormMount || $[46] !== onSearchFormSubmit || $[47] !== searchForm || $[48] !== searchFormClassName || $[49] !== searchFormDefaultValue) {
    t20 = searchForm ? <Input ref={el => {
      if (!el) {
        return;
      }
      onSearchFormMount?.(el);
    }} key={searchFormDefaultValue} type="search" placeholder={"\uAC80\uC0C9\uC5B4\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694"} dimensions="H40" defaultValue={searchFormDefaultValue} className={cn("my-12 w-full border-none bg-Gray-200 text-Gray-900 placeholder:text-Gray-550 focus:border-none", searchFormClassName)} onClick={() => onSearchFormClick?.()} onKeyDown={e => {
      if (e.key === "Enter") {
        onSearchFormSubmit?.(e.currentTarget.value);
      }
    }} onClear={() => onSearchFormSubmit?.("")} /> : null;
    $[44] = onSearchFormClick;
    $[45] = onSearchFormMount;
    $[46] = onSearchFormSubmit;
    $[47] = searchForm;
    $[48] = searchFormClassName;
    $[49] = searchFormDefaultValue;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== t16 || $[52] !== t17 || $[53] !== t18 || $[54] !== t19 || $[55] !== t20) {
    t21 = <div className="z-[1] flex grow items-center gap-20 pl-20">{t16}{t17}{t18}{t19}{t20}</div>;
    $[51] = t16;
    $[52] = t17;
    $[53] = t18;
    $[54] = t19;
    $[55] = t20;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== searchButton || $[58] !== textColor) {
    t22 = searchButton ? <Button aria-label={"\uAC80\uC0C9\uC73C\uB85C \uC774\uB3D9"} className="relative size-26 p-0" dimensions="H26" theme="primary-text" asChild={true} {...gtmDatasets.clickGnb({
      menu_name: "\uAC80\uC0C9"
    })}><CustomLink href={typeof searchButton === "boolean" ? ROUTES.SEARCH.pathname : searchButton}><SvgMagnifyingGlass className={cn("size-24", textColor)} /></CustomLink></Button> : null;
    $[57] = searchButton;
    $[58] = textColor;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== bellButton || $[61] !== notification || $[62] !== safeRouter || $[63] !== textColor) {
    t23 = bellButton ? <Button aria-label={"\uC54C\uB9BC\uC73C\uB85C \uC774\uB3D9"} className="relative size-26 p-0" dimensions="H26" theme="primary-text" onClick={() => {
      safeRouter.push(ROUTES.PUSH.pathname);
    }}><SvgNotification className={cn("size-24", textColor)} />{notification ? <span className="absolute right-14 top-12 box-content size-4 rounded-pill border-2 border-Gray-100 bg-R-500" /> : null}</Button> : null;
    $[60] = bellButton;
    $[61] = notification;
    $[62] = safeRouter;
    $[63] = textColor;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  let t24;
  if ($[65] !== handleShareLink || $[66] !== shareButton || $[67] !== textColor) {
    t24 = shareButton ? <Button aria-label={"\uACF5\uC720\uD558\uAE30"} className="relative size-26 p-0" dimensions="H26" theme="primary-text" onClick={() => {
      handleShareLink(location.href);
    }}><SvgExpandWindow className={cn("size-24", textColor)} /></Button> : null;
    $[65] = handleShareLink;
    $[66] = shareButton;
    $[67] = textColor;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] !== safeRouter || $[70] !== settingsButton || $[71] !== textColor) {
    t25 = settingsButton ? <Button aria-label={"\uC124\uC815"} className="relative size-26 p-0" dimensions="H26" theme="primary-text" onClick={() => safeRouter.push(ROUTES.MY_SETTINGS.pathname)}><SvgSetting className={cn("size-24", textColor)} /></Button> : null;
    $[69] = safeRouter;
    $[70] = settingsButton;
    $[71] = textColor;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  let t26;
  if ($[73] !== kebabButton || $[74] !== onClickKebab || $[75] !== textColor) {
    t26 = kebabButton ? typeof kebabButton === "boolean" ? <Button aria-label={"\uB354\uBCF4\uAE30"} className="relative size-26 p-0" dimensions="H26" theme="primary-text" onClick={onClickKebab}><SvgKebab className={cn("size-24", textColor)} /></Button> : kebabButton : null;
    $[73] = kebabButton;
    $[74] = onClickKebab;
    $[75] = textColor;
    $[76] = t26;
  } else {
    t26 = $[76];
  }
  let t27;
  if ($[77] !== handleCloseConfirm || $[78] !== handleConfirmAndClose || $[79] !== handleOpenConfirm || $[80] !== isToss || $[81] !== modalOpened) {
    t27 = isToss ? <><Button aria-label={"\uD1A0\uC2A4\uB85C \uC774\uB3D9"} className="relative h-26 p-0" dimensions="H40" theme="primary-text" onClick={handleOpenConfirm}>닫기</Button><TossExitConfirmModal opened={modalOpened} onClose={handleCloseConfirm} onConfirm={handleConfirmAndClose} /></> : null;
    $[77] = handleCloseConfirm;
    $[78] = handleConfirmAndClose;
    $[79] = handleOpenConfirm;
    $[80] = isToss;
    $[81] = modalOpened;
    $[82] = t27;
  } else {
    t27 = $[82];
  }
  let t28;
  if ($[83] !== children || $[84] !== t22 || $[85] !== t23 || $[86] !== t24 || $[87] !== t25 || $[88] !== t26 || $[89] !== t27) {
    t28 = <div className="z-[1] ml-auto flex justify-end gap-20 pr-20">{t22}{t23}{children}{t24}{t25}{t26}{t27}</div>;
    $[83] = children;
    $[84] = t22;
    $[85] = t23;
    $[86] = t24;
    $[87] = t25;
    $[88] = t26;
    $[89] = t27;
    $[90] = t28;
  } else {
    t28 = $[90];
  }
  let t29;
  if ($[91] !== t11 || $[92] !== t13 || $[93] !== t14 || $[94] !== t15 || $[95] !== t21 || $[96] !== t28) {
    t29 = <nav aria-label={"\uD398\uC774\uC9C0 \uB0B4\uBE44\uAC8C\uC774\uC158"} className={t11} style={t13}>{t14}{t15}{t21}{t28}</nav>;
    $[91] = t11;
    $[92] = t13;
    $[93] = t14;
    $[94] = t15;
    $[95] = t21;
    $[96] = t28;
    $[97] = t29;
  } else {
    t29 = $[97];
  }
  return t29;
};
export const PageNavigationHost = () => {
  const $ = _c(7);
  const pathname = usePathname() ?? "";
  let t0;
  if ($[0] !== pathname) {
    t0 = state => state.contentProps?.pathname === pathname ? state.contentProps : state.contentPropsByPathname[pathname] ?? null;
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const contentProps = usePageNavigationStore(t0);
  if (!contentProps) {
    return null;
  }
  let t1;
  if ($[2] !== contentProps.className) {
    t1 = cn("relative hidden mobile-tablet:flex", contentProps.className);
    $[2] = contentProps.className;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== contentProps || $[5] !== t1) {
    t2 = <PageNavigationContent {...contentProps} className={t1} />;
    $[4] = contentProps;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
export interface PageNavigationProps {
  className?: string;
  title?: string | ReactNode;
  titleLeft?: string | ReactNode;
  children?: ReactNode;
  logo?: 'full' | 'symbol';
  logoHref?: string;
  searchButton?: boolean | string;
  bellButton?: boolean;
  shareButton?: boolean;
  notification?: boolean;
  settingsButton?: boolean;
  backButton?: boolean;
  backButtonType?: 'exit' | 'back';
  /** backButton 유무와 관계없이 뒤로가기 활성화 */
  enableHistoryBack?: boolean;
  kebabButton?: boolean | ReactNode;
  onClickKebab?: VoidFunction;
  hideHomeButton?: boolean;
  onClickBack?: VoidFunction | string;
  style?: React.CSSProperties;
  textColor?: string;
  isNotUsedHomeButton?: boolean;
  donationHomeButton?: boolean;
  /** logo가 true일 때 렌더링 되는 video backgroundColor에 영향을 줌 */
  backgroundColor?: 'gray';
  //* 검색 폼 관련
  searchForm?: boolean;
  searchFormDefaultValue?: string;
  searchFormClassName?: string;
  onSearchFormMount?: (ref: HTMLInputElement) => void;
  onSearchFormClick?: () => void;
  onSearchFormSubmit?: (value: string) => void;
}
export const PageNavigation = (props: PageNavigationProps) => {
  const {
    logo,
    logoHref,
    title,
    titleLeft,
    children,
    backButton = true,
    backButtonType = 'back',
    enableHistoryBack,
    searchButton,
    bellButton,
    notification,
    shareButton,
    settingsButton,
    kebabButton,
    onClickBack,
    onClickKebab,
    hideHomeButton,
    className,
    style,
    textColor,
    isNotUsedHomeButton,
    donationHomeButton,
    backgroundColor,
    searchForm,
    searchFormDefaultValue,
    searchFormClassName,
    onSearchFormMount,
    onSearchFormClick,
    onSearchFormSubmit
  } = props;
  const router = useCustomRouter();
  const {
    openConfirmModal,
    closeConfirmModal,
    confirmAndClose,
    isModalOpened
  } = useTossWebView();
  const setShareLink = useGlobalStore(state => state.setShareLink);
  const [isKbWebview, setAppModal, isToss] = useDeviceInfoStore(useShallow(state_0 => [state_0.webviewType === WEBVIEW_TYPE.KB_PAY, state_0.setAppModal, state_0.subDomain === SUB_DOMAIN.TOSS] as const));
  useEffect(() => {
    const _window = window as FlutterInAppWebViewWindow;
    if (_window.router) {
      _window.router.back = backButton || enableHistoryBack ? () => router.back() : () => {
        setAppModal({
          isOpened: true,
          type: 'exit'
        });
      };
      return () => {
        _window.router.back = () => {};
      };
    }
  }, [backButton, enableHistoryBack, router, setAppModal]);
  const pathname = usePathname() ?? '';
  const shouldHideNavigation = isKbWebview && pathname !== MODULE_ROUTES.KB_HOME.pathname;
  const ownerIdRef = useRef<number | null>(null);
  const ownerVersionRef = useRef(0);
  const pathnameRef = useRef(pathname);
  if (ownerIdRef.current === null) {
    ownerIdRef.current = createPageNavigationOwnerId();
  }

  // eslint-disable-next-line react-hooks/refs -- 최신값 ref 패턴, stale closure 방지를 위해 렌더마다 동기화
  pathnameRef.current = pathname;
  const contentProps: PageNavigationContentProps = {
    logo,
    logoHref,
    title,
    titleLeft,
    children,
    backButton,
    backButtonType,
    enableHistoryBack,
    searchButton,
    bellButton,
    notification,
    shareButton,
    settingsButton,
    kebabButton,
    onClickBack,
    onClickKebab,
    hideHomeButton,
    className,
    style,
    textColor,
    isNotUsedHomeButton,
    donationHomeButton,
    backgroundColor,
    searchForm,
    searchFormDefaultValue,
    searchFormClassName,
    onSearchFormMount,
    onSearchFormClick,
    onSearchFormSubmit,
    isToss,
    isKbWebview,
    pathname,
    setShareLink,
    router,
    openConfirmModal,
    closeConfirmModal,
    confirmAndClose,
    isModalOpened
  };
  useIsomorphicLayoutEffect(() => {
    if (shouldHideNavigation) {
      usePageNavigationStore.getState().clearContent();
      return;
    }
    const ownerId = ownerIdRef.current;
    if (ownerId === null) return;
    ownerVersionRef.current += 1;
    const ownerVersion = ownerVersionRef.current;
    usePageNavigationStore.getState().setContent(ownerId, ownerVersion, contentProps);
  }, [contentProps, shouldHideNavigation]);
  useIsomorphicLayoutEffect(() => {
    const ownerId_0 = ownerIdRef.current;
    return () => {
      if (ownerId_0 === null) return;
      if (window.location.pathname !== pathnameRef.current) return;
      usePageNavigationStore.getState().clearContent(ownerId_0, ownerVersionRef.current);
    };
  }, []);
  return null;
};
function _temp() {}
function _temp2() {}
function _temp3() {}